



























import { Component, Vue, Prop, Emit } from 'vue-property-decorator';
import {
  MiscellaneousComparisonTitleDetails,
  MonthComparisonTitleDetails,
  SummaryAnalysis,
  SummaryAnalysisOutput,
} from '@/models/swine/analysis.model';
import BasicBarChart from '../reports/BasicBarChart.vue';
import GraphToggle from './GraphToggle.vue';
import AnalysisLayout from '@/components/swine/layout/AnalysisLayout.vue';
import AbscessScarringPericarditisTable from './AbscessScarringPericarditisAnalysisTable.vue';
import {
  ABSCESS_SCARRING_PERICARDITIS_COLORS,
  ELANCO_LOGO_IMAGE,
  LUNG_LESION_IMAGE,
} from '@/utils/analysisPdfMake';
import { convertDateToDDMMYYYYWithSlashes } from '@/utils/moment';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
(pdfMake as any).vfs = pdfFonts.pdfMake.vfs;
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { addDecimalPointToNumbers } from '@/utils/formats';

@Component({
  components: {
    BasicBarChart,
    GraphToggle,
    AnalysisLayout,
    AbscessScarringPericarditisTable,
  },
})
export default class AbscessScarringPericarditisComponent extends Vue {
  @Prop({})
  chartData!: Chart.ChartData;

  @Prop({})
  options!: Chart.ChartOptions;

  @Prop({})
  analysisResponse!: SummaryAnalysisOutput;

  @Prop({})
  comparisonTitle!: MiscellaneousComparisonTitleDetails &
    MonthComparisonTitleDetails;

  @Prop({})
  isMiscellaneous!: boolean;

  @Prop({ required: true })
  isCurrentRoleViewer!: boolean;

  removeNumberOfLungsExamined = false;

  get title() {
    return `Hide No. of Lungs/Hearts Examined`;
  }

  updateRemoveNumberOfLungsExamined(value: boolean) {
    this.removeNumberOfLungsExamined = value;
  }

  @Emit(`changeChart`)
  changeGraph(value: boolean) {
    return value;
  }

  generateChartImage() {
    const currentDocument: HTMLCanvasElement | null = document.getElementById(
      'bar-chart',
    ) as HTMLCanvasElement;
    if (currentDocument) {
      return currentDocument.toDataURL();
    }
    return '';
  }

  generatePDFBodyTitle() {
    let generatedTitle = `Comparison of ${this.comparisonTitle.selectedOption} by ${this.comparisonTitle.selectedComparison} from ${this.comparisonTitle.startDate} and ${this.comparisonTitle.endDate} `;
    if (this.comparisonTitle.queries) {
      generatedTitle += `between `;
      this.comparisonTitle.queries.forEach((detail, index) => {
        if (index < this.comparisonTitle.queries.length - 1) {
          generatedTitle += `${detail}, `;
        } else {
          generatedTitle += `and ${detail}.`;
        }
      });
    }

    if (this.comparisonTitle.farmUnitCodeName) {
      generatedTitle += `on ${this.comparisonTitle.farmUnitCodeName}.`;
    }

    return generatedTitle;
  }

  generatePDFTableHeader() {
    return [
      {
        text: '',
        fillColor: ABSCESS_SCARRING_PERICARDITIS_COLORS.LIGHT_GREY.BACKGROUND,
        color: ABSCESS_SCARRING_PERICARDITIS_COLORS.LIGHT_GREY.TEXT,
        fontSize: 9,
        border: [false, false, false, true],
        bold: true,
        marginTop: 8,
        alignment: `center`,
        height: 40,
      },
      {
        text: 'No. of Lungs Examined',
        fillColor: ABSCESS_SCARRING_PERICARDITIS_COLORS.DARK_GREY.BACKGROUND,
        color: ABSCESS_SCARRING_PERICARDITIS_COLORS.DARK_GREY.TEXT,
        fontSize: 9,
        border: [false, false, false, true],
        bold: true,
        marginTop: 8,
        alignment: `center`,
        height: 40,
      },
      {
        text: 'Abscess (%)',
        fillColor: ABSCESS_SCARRING_PERICARDITIS_COLORS.GREEN.BACKGROUND,
        color: ABSCESS_SCARRING_PERICARDITIS_COLORS.GREEN.TEXT,
        fontSize: 9,
        border: [false, false, false, true],
        bold: true,
        marginTop: 8,
        alignment: `center`,
        height: 40,
      },
      {
        text: 'No. of Lungs Examined',
        fillColor: ABSCESS_SCARRING_PERICARDITIS_COLORS.DARK_GREY.BACKGROUND,
        color: ABSCESS_SCARRING_PERICARDITIS_COLORS.DARK_GREY.TEXT,
        fontSize: 9,
        border: [false, false, false, true],
        bold: true,
        marginTop: 8,
        alignment: `center`,
        height: 40,
      },
      {
        text: 'Scarring/Chronic (%)',
        fillColor: ABSCESS_SCARRING_PERICARDITIS_COLORS.YELLOW_LIGHT.BACKGROUND,
        color: ABSCESS_SCARRING_PERICARDITIS_COLORS.YELLOW_LIGHT.TEXT,
        fontSize: 9,
        border: [false, false, false, true],
        bold: true,
        marginTop: 8,
        alignment: `center`,
        height: 40,
      },
      {
        text: 'No. of Lungs Examined',
        fillColor: ABSCESS_SCARRING_PERICARDITIS_COLORS.DARK_GREY.BACKGROUND,
        color: ABSCESS_SCARRING_PERICARDITIS_COLORS.DARK_GREY.TEXT,
        fontSize: 9,
        border: [false, false, false, true],
        bold: true,
        marginTop: 8,
        alignment: `center`,
        height: 40,
      },
      {
        text: 'Pericarditis (%)',
        fillColor: ABSCESS_SCARRING_PERICARDITIS_COLORS.RED_LIGHT.BACKGROUND,
        color: ABSCESS_SCARRING_PERICARDITIS_COLORS.RED_LIGHT.TEXT,
        fontSize: 9,
        border: [false, false, false, true],
        bold: true,
        marginTop: 8,
        alignment: `center`,
        height: 40,
      },
    ];
  }

  generatePDFTableContent() {
    if (this.analysisResponse) {
      const tableData = [];
      if (this.analysisResponse.farmUnits) {
        tableData.push(...this.analysisResponse.farmUnits);
      }
      if (this.analysisResponse.industryAverages) {
        tableData.push(...this.analysisResponse.industryAverages);
      }
      if (this.analysisResponse.individualReports) {
        tableData.push(...this.analysisResponse.individualReports);
      }

      const pdfTableDetails = tableData.map((data: SummaryAnalysis) => {
        if (
          data &&
          data.identifier &&
          data.numberOfLungsExaminedForPericarditis &&
          data.numberOfLungsExaminedForAbscess &&
          data.numberOfLungsExaminedForScarring &&
          data.abscess &&
          data.scarring &&
          data.pericarditis
        ) {
          return [
            {
              text: data.identifier,
              fillColor:
                ABSCESS_SCARRING_PERICARDITIS_COLORS.LIGHT_GREY.BACKGROUND,
              color: ABSCESS_SCARRING_PERICARDITIS_COLORS.LIGHT_GREY.TEXT,
              fontSize: 10,
              alignment: `center`,
              border: [false, false, false, true],
              bold: true,
              height: 20,
              marginTop: 0,
            },
            {
              text: data.numberOfLungsExaminedForAbscess.toString(),
              fillColor:
                ABSCESS_SCARRING_PERICARDITIS_COLORS.DARK_GREY.BACKGROUND,
              color: ABSCESS_SCARRING_PERICARDITIS_COLORS.DARK_GREY.TEXT,
              fontSize: 10,
              alignment: `center`,
              border: [false, false, false, true],
              bold: true,
              height: 20,
              marginTop: 0,
            },
            {
              text: addDecimalPointToNumbers(data.abscess),
              fillColor: ABSCESS_SCARRING_PERICARDITIS_COLORS.GREEN.BACKGROUND,
              color: ABSCESS_SCARRING_PERICARDITIS_COLORS.GREEN.TEXT,
              fontSize: 10,
              alignment: `center`,
              border: [false, false, false, true],
              bold: true,
              height: 20,
              marginTop: 0,
            },
            {
              text: data.numberOfLungsExaminedForScarring.toString(),
              fillColor:
                ABSCESS_SCARRING_PERICARDITIS_COLORS.DARK_GREY.BACKGROUND,
              color: ABSCESS_SCARRING_PERICARDITIS_COLORS.DARK_GREY.TEXT,
              fontSize: 10,
              alignment: `center`,
              border: [false, false, false, true],
              bold: true,
              height: 20,
              marginTop: 0,
            },
            {
              text: addDecimalPointToNumbers(data.scarring),
              fillColor:
                ABSCESS_SCARRING_PERICARDITIS_COLORS.YELLOW_LIGHT.BACKGROUND,
              color: ABSCESS_SCARRING_PERICARDITIS_COLORS.YELLOW_LIGHT.TEXT,
              fontSize: 10,
              alignment: `center`,
              border: [false, false, false, true],
              bold: true,
              height: 20,
              marginTop: 0,
            },
            {
              text: data.numberOfLungsExaminedForPericarditis.toString(),
              fillColor:
                ABSCESS_SCARRING_PERICARDITIS_COLORS.DARK_GREY.BACKGROUND,
              color: ABSCESS_SCARRING_PERICARDITIS_COLORS.DARK_GREY.TEXT,
              fontSize: 10,
              alignment: `center`,
              border: [false, false, false, true],
              bold: true,
              height: 20,
              marginTop: 0,
            },
            {
              text: addDecimalPointToNumbers(data.pericarditis),
              fillColor:
                ABSCESS_SCARRING_PERICARDITIS_COLORS.RED_LIGHT.BACKGROUND,
              color: ABSCESS_SCARRING_PERICARDITIS_COLORS.RED_LIGHT.TEXT,
              fontSize: 10,
              alignment: `center`,
              border: [false, false, false, true],
              bold: true,
              height: 20,
              marginTop: 0,
            },
          ];
        }
      });
      return pdfTableDetails;
    }
  }

  generatePdfTable() {
    const pdfTable = [this.generatePDFTableHeader()];
    const details = this.generatePDFTableContent();

    if (details) {
      details.forEach((item) => {
        if (item) {
          pdfTable.push(item);
        }
      });
    }
    return pdfTable;
  }

  generatePDF() {
    try {
      const imgElancoLogo = ELANCO_LOGO_IMAGE;
      const imgLungLesion = LUNG_LESION_IMAGE;
      const headerTitle = 'Lung Lesion Scoring Analysis';
      pdfMake.tableLayouts = {
        headerLayout: {
          hLineColor: function () {
            return '#dbdbdb';
          },
          vLineColor: function () {
            return '';
          },
          paddingLeft: function () {
            return 0;
          },
          paddingRight: function () {
            return 0;
          },
          paddingTop: function () {
            return 0;
          },
          paddingBottom: function () {
            return 0;
          },
        },
        tableLayout: {
          hLineColor: function () {
            return '#dbdbdb';
          },
          vLineColor: function () {
            return '';
          },
        },
      };

      const docDefinition: TDocumentDefinitions = {
        pageOrientation: 'landscape',
        content: [
          {
            table: {
              widths: ['auto', 'auto', '*'],
              body: [
                [
                  {
                    svg: imgElancoLogo,
                    width: 100,
                    margin: [0, 0, 0, 0],
                    border: [false, false, false, true],
                  },
                  {
                    svg: imgLungLesion,
                    width: 50,
                    margin: [120, 0, 0, 0],
                    border: [false, false, false, true],
                  },
                  {
                    text: headerTitle,
                    color: '#333',
                    bold: true,
                    fontSize: 20,
                    margin: [17, 17, 0, 17],
                    border: [false, false, false, true],
                  },
                ],
              ],
            },
            layout: 'headerLayout',
          },
          {
            text: this.generatePDFBodyTitle(),
            bold: true,
            fontSize: 14,
            margin: [0, 30, 0, 30],
          },
          {
            image: this.generateChartImage(),
            width: 741,
          },
          {
            text: '',
            pageBreak: 'after',
          },
          {
            table: {
              widths: ['auto', 'auto', '*'],
              body: [
                [
                  {
                    svg: imgElancoLogo,
                    width: 100,
                    margin: [0, 0, 0, 0],
                    border: [false, false, false, true],
                  },
                  {
                    svg: imgLungLesion,
                    width: 50,
                    margin: [120, 0, 0, 0],
                    border: [false, false, false, true],
                  },
                  {
                    text: headerTitle,
                    color: '#333',
                    bold: true,
                    fontSize: 20,
                    margin: [17, 17, 0, 17],
                    border: [false, false, false, true],
                  },
                ],
              ],
            },
            layout: 'headerLayout',
          },
          {
            text: this.generatePDFBodyTitle(),
            bold: true,
            fontSize: 14,
            margin: [0, 30, 0, 30],
          },
          {
            table: {
              widths: ['*', 110, 110, 110, 110, 110, 110],
              body: this.generatePdfTable(),
            },
            layout: 'tableLayout',
          },
        ],
        footer: {
          columns: [
            {
              text: `Exported on ${convertDateToDDMMYYYYWithSlashes(
                new Date(),
              )}`,
              fontSize: 10,
              color: '#8e8e8e',
              margin: [30, 0, 0, 0],
            },
          ],
        },
      };
      pdfMake
        .createPdf(docDefinition)
        .download('lung-lesion-scoring-analysis.pdf');
    } catch (error) {
      console.error(error);
    }
  }
}
